import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Input } from './input';

interface CurrencyInputProps extends React.ComponentProps<typeof Input> {
  value?: number;
  onValueChange?: (value: number) => void;
  maxDecimalPlaces?: number;
  maxIntegerPlaces?: number;
  placeholder?: string;
}

export function CurrencyInput({
  value,
  onValueChange,
  maxDecimalPlaces = 4,
  maxIntegerPlaces = 2,
  placeholder = '0.0000',
  className,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when prop value changes
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value && value > 0 ? value.toString() : '');
    }
  }, [value, isFocused]);

  const formatDisplayValue = (val: string): string => {
    if (!val) return '';
    
    // Remove any non-digit and non-decimal point characters
    let cleaned = val.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit integer part
    if (parts[0] && parts[0].length > maxIntegerPlaces) {
      parts[0] = parts[0].slice(0, maxIntegerPlaces);
      cleaned = parts.join('.');
    }
    
    // Limit decimal places
    if (parts[1] && parts[1].length > maxDecimalPlaces) {
      parts[1] = parts[1].slice(0, maxDecimalPlaces);
      cleaned = parts[0] + '.' + parts[1];
    }
    
    return cleaned;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatDisplayValue(rawValue);
    setDisplayValue(formatted);
    
    const numValue = parseFloat(formatted);
    if (onValueChange) {
      onValueChange(isNaN(numValue) ? 0 : numValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Format the final value
    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue) && numValue > 0) {
      setDisplayValue(numValue.toFixed(maxDecimalPlaces));
    } else {
      setDisplayValue('');
    }
    props.onBlur?.(e);
  };

  return (
    <div className="relative">
      <Input
        {...props}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn('text-right font-mono', className)}
        autoComplete="off"
      />
      {!isFocused && !displayValue && (
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <span className="text-muted-foreground font-mono text-sm">
            {placeholder}
          </span>
        </div>
      )}
    </div>
  );
}